import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon, ChevronLeftIcon } from 'lucide-react';
import { KnowledgeMap } from '../KnowledgeMap';
import { StatusPill } from '../../ui/StatusPill';
import { evidence, samplePassage, sampleRelationship } from '../../../data/sampleResearch';
interface HeroReviewProps {
  approved: boolean;
  onApprove: () => void;
  onBack: () => void;
}
const ease = [0.23, 1, 0.32, 1] as const;
export function HeroReview({
  approved,
  onApprove,
  onBack
}: HeroReviewProps) {
  return <div className="flex h-full flex-col gap-3" data-id="element-240" data-mp="2v9esy25tryf">
      <div className="flex items-center gap-2 text-[12px]" data-id="element-241" data-mp="3kafvtg2n0ho2">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-ink-muted transition-colors duration-150 hover:bg-ink/[0.05] hover:text-ink" data-id="element-242" data-mp="3k7tptkveaa7y">
          <ChevronLeftIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-243" data-mp="3g71cogjejl6e" /> Review queue
        </button>
        <span className="text-ink-faint" data-id="element-244" data-mp="3czv36kocg9ni">/</span>
        <span className="font-medium" data-id="element-245" data-mp="3d2r0q7wa4wbf">Finding 1 of 12</span>
      </div>

      <div className="grid flex-1 gap-3 md:grid-cols-2" data-id="element-246" data-mp="38yrkzritq1j4">
        <section className="flex flex-col rounded-2xl bg-white/80 p-4 ring-1 ring-ink/[0.05]" aria-label="Source passage" data-id="element-247" data-mp="3mzlwij164im0">
          <p className="text-xs font-medium text-ink-muted" data-id="element-248" data-mp="3i1aomdufoi6f">Source passage</p>
          <p className="mt-2 text-[13px] font-semibold leading-snug" data-id="element-249" data-mp="2u9vehywf0wkm">{samplePassage.source}</p>
          <p className="mt-0.5 text-[11px] text-ink-muted" data-id="element-250" data-mp="2pbtyjmuadddd">{samplePassage.location}</p>
          <p className="mt-3 text-[13px] leading-relaxed text-ink-soft" data-id="element-251" data-mp="33zy9g4n3kkll">
            {samplePassage.before}
            <mark className="mark-evidence" data-id="element-252" data-mp="2z8zr6zijtcfp">{samplePassage.highlight}</mark>
            {samplePassage.after}
          </p>
          <p className="mt-auto pt-3 text-[11px] text-ink-faint" data-id="element-253" data-mp="2bivoqvh3jiqa">{samplePassage.journal}</p>
        </section>

        <section className="relative flex flex-col overflow-hidden rounded-2xl bg-white p-4 shadow-glow" aria-label="Proposed relationship" data-id="element-254" data-mp="26k137lrqvg2h">
          <AnimatePresence mode="wait" initial={false} data-id="element-255" data-mp="fgskgioa1fec">
            {approved ? <motion.div key="approved" initial={{
            opacity: 0,
            scale: 0.98
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.22,
            ease
          }} className="flex h-full flex-col" data-id="element-256" data-mp="feo5n5ouavza">
                <div className="flex items-center gap-2" data-id="element-257" data-mp="3snapc3yzr7qb">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-ok text-white" data-id="element-258" data-mp="3snu8fo0do25x">
                    <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-259" data-mp="i6zcsvic7jl" />
                  </span>
                  <p className="text-sm font-semibold" data-id="element-260" data-mp="5jw6vyjiu32x">Finding approved</p>
                </div>
                <p className="mt-1 text-[12px] text-ink-muted" data-id="element-261" data-mp="1byf51368jyzh">Added to the knowledge map · version 14</p>
                <KnowledgeMap showNew className="mt-1 h-[150px] w-full" data-id="element-262" data-mp="1gzuowrwofzga" />
              </motion.div> : <motion.div key="pending" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0,
            scale: 0.98
          }} transition={{
            duration: 0.18,
            ease
          }} className="flex h-full flex-col" data-id="element-263" data-mp="1m2lp1sbzoepo">
                <div className="flex items-center justify-between" data-id="element-264" data-mp="18axfnwt4k4ce">
                  <p className="text-xs font-medium text-ink-muted" data-id="element-265" data-mp="18945i7jaz1zn">Proposed relationship</p>
                  <StatusPill tone="warn" data-id="element-266" data-mp="13asx818nozjj">Awaiting review</StatusPill>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-1.5 text-[12px]" data-id="element-267" data-mp="ydj7sll9onvp">
                  <span className="rounded-lg bg-ink px-2 py-1 font-medium text-white" data-id="element-268" data-mp="ucqy5r8hggyw">{sampleRelationship.from}</span>
                  <span className="inline-flex items-center gap-1 text-ink-muted" data-id="element-269" data-mp="2sq9397raa557">
                    {sampleRelationship.relation} <ArrowRightIcon className="h-3 w-3" aria-hidden="true" data-id="element-270" data-mp="2onm9h1b1ya5d" />
                  </span>
                  <span className="rounded-lg bg-ink px-2 py-1 font-medium text-white" data-id="element-271" data-mp="2vqu6xrap22av">{sampleRelationship.to}</span>
                </div>
                <ul className="mt-3 space-y-1.5" data-id="element-272" data-mp="2msqjuqk28xgy">
                  {evidence.map(e => <li key={e.source} className="flex items-center justify-between gap-2 text-[12px]" data-id="element-273" data-mp="2qt0xmpe3gs6d">
                      <span className="truncate" data-id="element-274" data-mp="2d1vuveffjjrt">
                        <span className="font-medium" data-id="element-275" data-mp="2d02om08qb1kc">{e.source}</span>{' '}
                        <span className="text-ink-muted" data-id="element-276" data-mp="2hugnwdqihsua">· {e.detail}</span>
                      </span>
                      <StatusPill tone={e.tone} data-id="element-277" data-mp="235kf2egmsbsm">{e.stance}</StatusPill>
                    </li>)}
                </ul>
                <div className="mt-auto flex gap-2 pt-3" data-id="element-278" data-mp="1u3b6vv8u4uie">
                  <button type="button" onClick={onApprove} className="inline-flex h-8 items-center rounded-full bg-accent px-3.5 text-[12px] font-medium text-white transition-colors duration-150 hover:bg-accent-ink" data-id="element-279" data-mp="1u3tc3hdh6j2g">
                    Approve finding
                  </button>
                  <button type="button" className="inline-flex h-8 items-center rounded-full px-3 text-[12px] font-medium text-ink-soft ring-1 ring-ink/10 transition-colors duration-150 hover:bg-ink/[0.04]" data-id="element-280" data-mp="1ohsjlqa795zg">
                    Request changes
                  </button>
                </div>
              </motion.div>}
          </AnimatePresence>
        </section>
      </div>
    </div>;
}